/**
 * Page of subscription selection.
 *
 * Copyright © Ircam Amplify 2025-2026
 */
import { API, fetchCompanyConsumption } from '@/api'
import FullScreenLayout from '@/components/FullScreenLayout'
import { DefaultSelectTier } from '@/components/SelectTier/DefaultSelectTier'
import { TopUpSelectTier } from '@/components/SelectTier/TopUpSelectTier'
import { useSelector } from '@/store'
import { TierType } from '@amplify3/duplo-back-end'
import { Box, LinearProgress } from '@mui/material'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { SubscriptionData } from '@amplify3/duplo-back-end'

/**
 * A progress bar with the title of the page, centered in the window.
 */
const TitleLinearProgress = () => {
  return (
    <FullScreenLayout
      className="!h-auto min-h-screen bg-white text-black"
      title="Simple, transparent pricing"
    >
      <Box
        sx={{
          flex: 1, // take remaining height below title
          display: 'flex',
          alignItems: 'center', // vertical center
          justifyContent: 'center', // horizontal center
        }}
      >
        <LinearProgress
          sx={{
            width: '50%', // half of container
            mx: 'auto',
            my: 32,
            backgroundColor: '#ffeae6',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#f67d69',
            },
          }}
        />
      </Box>
    </FullScreenLayout>
  )
}

/**
 * The page of the tier selection.
 *
 * It currently depends on the type of subsctiption.
 * When only top-up and enterprise subscription are available,
 * the TitleLinearProgress and DefaultSelectTier must be removed.
 */
export const SelectTier = () => {
  const [prices, setPrices] = useState<SubscriptionData | null>(null)

  const userTier = useSelector(
    (s) => s.user.companyConsumption?.subscription_tier
  )

  useEffect(() => {
    // Get prices only once, at the loading of the page
    fetchCompanyConsumption()
    const getPrices = async () => {
      const { data } = await API(await fetchAuthSession()).get(
        '/checkout/prices'
      )
      if (data && data.prices) {
        setPrices(data.prices)
      }
    }

    getPrices()
  }, [])

  return !userTier ? (
    <TitleLinearProgress />
  ) : userTier == TierType.TopUpCreditAimd ? (
    <TopUpSelectTier userTier={userTier} />
  ) : (
    <DefaultSelectTier prices={prices} />
  )
}
